import * as React from 'react'
import * as Sentry from '@sentry/browser'
import { Intent } from '@blueprintjs/core'
import Client from '../../../client'
import { BookResult, SortDecision } from '../types'
import { toaster } from '../../../Toast'
import { useAmplitude } from '../../../contexts/AmplitudeContext'

interface UseAmazonBookSearchConfig {
  initialSortDecision?: SortDecision
  onSortDecisionChange?: (decision: SortDecision, selectedBook: BookResult | null, selectedIndex: number | null) => void
  onBookSelected?: (book: BookResult, index: number) => void
}

export const useAmazonBookSearchBase = (config: UseAmazonBookSearchConfig = {}) => {
  const { initialSortDecision = 'scan', onSortDecisionChange, onBookSelected } = config

  const [query, setQuery] = React.useState('')
  const [inputquery, setInputquery] = React.useState('')
  const [books, setBooks] = React.useState<BookResult[]>([])
  const [loading, setLoading] = React.useState(false)
  const [loadingSort, setLoadingSort] = React.useState(false)
  const [selectedBookIndex, setSelectedBookIndex] = React.useState<number | null>(null)
  const [sortDecision, setSortDecision] = React.useState<SortDecision>(initialSortDecision)
  const scanDataRef =
    React.useRef<Pick<BookResult, 'timeToReturnResult' | 'timeToScan' | 'scanResult' | 'numResultsReturned'>>()

  const amplitude = useAmplitude()

  React.useEffect(() => {
    if (onSortDecisionChange) {
      const selectedBook = selectedBookIndex !== null ? books[selectedBookIndex] : null
      const enrichedBook =
        selectedBook && scanDataRef.current
          ? {
              ...selectedBook,
              ...scanDataRef.current
            }
          : selectedBook

      onSortDecisionChange(sortDecision, enrichedBook, selectedBookIndex)
    }
  }, [sortDecision, selectedBookIndex, books, onSortDecisionChange])

  const handleSelectBook = React.useCallback(
    async (index: number, bookData?: BookResult) => {
      setSelectedBookIndex(index)
      setSortDecision('scan')
      const book = bookData || books[index]
      setLoadingSort(true)

      if (onBookSelected) {
        onBookSelected(book, index)
      }

      try {
        const response = await Client.searchAmazonBooks({
          asin_query: book.asin,
          query: book.isbn
        })
        const decision = response.data.sortDecision || 'scan'
        setSortDecision(() => decision)

        amplitude.track('BookSelected', {
          isbn: book.isbn,
          asin: book.asin,
          sortDecision: decision,
          selectedIndex: index
        })
      } catch (err) {
        Sentry.captureException(err)

        let errorMessage = 'An unexpected error occurred while processing the book.'
        if (err?.response?.data?.error) {
          errorMessage = err.response.data.error
        } else if (err?.message) {
          errorMessage = err.message
        }

        toaster.show({
          message: errorMessage,
          intent: Intent.DANGER,
          timeout: 5000
        })

        setSelectedBookIndex(null)
        setSortDecision('multipleResults')
      } finally {
        setLoadingSort(false)
      }
    },
    [books, onBookSelected, amplitude]
  )

  const handleSearch = React.useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        return
      }
      setInputquery(searchQuery)
      setSortDecision('scan')
      setLoading(true)
      setSelectedBookIndex(null)

      const scanStartTime = performance.now()

      try {
        const response = await Client.searchAmazonBooks({
          query: searchQuery.trim()
        })

        const timeToScan = Math.round(performance.now() - scanStartTime)

        const booksData = response.data.books || []
        setBooks(booksData)

        scanDataRef.current = {
          timeToScan,
          timeToReturnResult: booksData[0]?.timeToReturnResult || 0,
          scanResult: response.data.sortDecision,
          numResultsReturned: booksData.length
        }

        amplitude.track('ItemScanned', {
          isbn: searchQuery.trim(),
          timeToReturnResult: booksData[0]?.timeToReturnResult || 0,
          timeToScan,
          scanResult: response.data.sortDecision,
          numResultsReturned: booksData.length
        })

        if (booksData.length > 1) setSortDecision('multipleResults')
        if (booksData.length === 1) {
          setSelectedBookIndex(0)
          setSortDecision(response.data.sortDecision || 'scan')
        }
      } catch (err) {
        Sentry.captureException(err)

        amplitude.track('ItemScanned', {
          isbn: searchQuery.trim(),
          timeToReturnResult: 0,
          timeToScan: Math.round(performance.now() - scanStartTime),
          scanResult: 'error',
          numResultsReturned: 0
        })

        let errorMessage = 'An unexpected error occurred while searching for books.'
        if (err?.response?.data?.error) {
          errorMessage = err.response.data.error
        } else if (err?.message) {
          errorMessage = err.message
        }

        toaster.show({
          message: errorMessage,
          intent: Intent.DANGER,
          timeout: 5000
        })
      } finally {
        setLoading(false)
        setQuery('')
      }
    },
    [amplitude]
  )

  const handleQueryChange = React.useCallback((value: string) => {
    setQuery(value)
  }, [])

  const resetSearch = React.useCallback(() => {
    setQuery('')
    setInputquery('')
    setBooks([])
    setSelectedBookIndex(null)
    setSortDecision(initialSortDecision)
  }, [initialSortDecision])

  return {
    query,
    inputquery,
    books,
    loading,
    loadingSort,
    selectedBookIndex,
    sortDecision,
    handleSelectBook,
    handleSearch,
    handleQueryChange,
    resetSearch,
    setSortDecision,
    setSelectedBookIndex,
    setBooks
  }
}
